import { Colors, Icon } from '@amelio/ui-library';

import { AlreadyHeldMention } from './AlreadyHeldMention';
import { EmployeeLevelMenu } from './EmployeeLevelMenu';

import type { EmployeeLevels } from './hooks/useProfileSelectorState';

import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { getTextByLanguage } from '@/utils/localization';

/** A row that does not count for the employee here: removed for them, or already held. */
export const GREYED_ROW_SURFACE = 'border-[#dbe0e6] bg-[#f5f7f9] opacity-60';

type CompetencyData = {
    id: string;
    name: Record<string, string>;
    description: Record<string, string>;
};

type CompetencyRowProps = {
    competency: CompetencyData;
    isPriority: boolean;
    isRemoved: boolean;
    /** The employee already holds it in their current profile: shown greyed, and said so. */
    isHeld?: boolean;
    /** Offers to remove the competency for this employee, or to restore it; absent, the row only reads. */
    onToggleRemoved?: () => void;
    /** Absent, no level is offered for the employee. */
    employeeLevels?: EmployeeLevels;
};

export const CompetencyRow = ({
    competency,
    isPriority,
    isRemoved,
    isHeld = false,
    onToggleRemoved,
    employeeLevels,
}: CompetencyRowProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();

    const readSurface = () => {
        if (isRemoved || isHeld) {
            return GREYED_ROW_SURFACE;
        }
        if (isPriority) {
            return 'bg-primary/10 border-primary/20';
        }
        return 'border-gray-100 bg-white/40 hover:border-gray-200';
    };

    return (
        <div
            className={
                'group flex min-h-[42px] flex-col items-center justify-between gap-3 rounded-lg border px-3 py-2 transition-all sm:flex-row ' +
                readSurface()
            }
            data-testid={`roster-selector-competency-${competency.id}`}
        >
            <div className={'min-w-0 flex-1'}>
                <div className={'flex flex-col items-center gap-2 sm:flex-row'}>
                    <p className={'text-navy font-medium' + (isRemoved ? ' line-through' : '')}>
                        {getTextByLanguage(competency.name, language)}
                    </p>

                    {isPriority && (
                        <span
                            className={
                                'bg-primary flex shrink-0 items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-white'
                            }
                        >
                            <Icon
                                name={'support-star'}
                                size={'xs'}
                                color={Colors.WHITE}
                            />
                            {t('competencies.competencyProfilesAdmin.selector.priorityBadge')}
                        </span>
                    )}

                    {isHeld && <AlreadyHeldMention />}
                </div>
            </div>

            {onToggleRemoved && (
                <button
                    type={'button'}
                    onClick={onToggleRemoved}
                    className={
                        'shrink-0 text-xs font-medium transition-opacity focus:opacity-100 ' +
                        (isRemoved
                            ? 'text-[#16a34a]'
                            : 'text-error opacity-0 group-hover:opacity-100')
                    }
                    data-testid={`roster-selector-remove-${competency.id}`}
                >
                    {isRemoved
                        ? t('competencies.competencyProfilesAdmin.selector.restore')
                        : t('competencies.competencyProfilesAdmin.selector.remove')}
                </button>
            )}

            {/* A removed competency is not the employee's anymore: no level is set for it. */}
            {employeeLevels && !isRemoved && (
                <EmployeeLevelMenu
                    competencyId={competency.id}
                    levelId={employeeLevels.levelOf(competency.id)}
                    onSelect={(levelId) => employeeLevels.select(competency.id, levelId)}
                />
            )}
        </div>
    );
};
